import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * WorksheetsToValidate - Widget "Feuilles à valider" pour RH
 */
const WorksheetsToValidate = ({ token, onNavigate }) => {
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWorksheets = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/worksheets/for-validation`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const data = await response.json();
        setWorksheets(data.success ? (data.data || []) : []);
      } catch (err) {
        console.error('Erreur fetch worksheets:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorksheets();
  }, [token]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Feuilles à valider</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Feuilles à valider</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>
          Feuilles à valider
          {worksheets.length > 0 && (
            <span style={styles.count}>({worksheets.length})</span>
          )}
        </h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('worksheets')}
        >
          Voir feuilles
        </button>
      </div>

      {worksheets.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>✅</span>
          <p style={styles.emptyText}>Aucune feuille en attente de validation</p>
        </div>
      ) : (
        <div style={styles.list}>
          {worksheets.slice(0, 5).map((ws, index) => (
            <div key={ws.id || index} style={styles.worksheetItem}>
              <div style={styles.worksheetInfo}>
                <span style={styles.groupName}>{ws.group_name || 'Groupe'}</span>
                <span style={styles.date}>{formatDate(ws.worksheet_date)}</span>
                <span style={styles.prestationNumber}>{ws.prestation_number}</span>
                {ws.participant_count && (
                  <span style={styles.participants}>({ws.participant_count} pers.)</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  count: {
    backgroundColor: '#6366f1',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  worksheetItem: {
    padding: '12px',
    backgroundColor: '#f0f4ff',
    borderRadius: '8px',
    border: '1px solid #c7d2fe'
  },
  worksheetInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },
  groupName: {
    fontWeight: '600',
    fontSize: '0.9rem',
    color: '#1a1a2e'
  },
  date: {
    fontSize: '0.85rem',
    color: '#6c757d'
  },
  prestationNumber: {
    fontSize: '0.85rem',
    color: '#6366f1',
    fontWeight: '500'
  },
  participants: {
    fontSize: '0.8rem',
    color: '#6c757d'
  }
};

export default WorksheetsToValidate;
